import type { QueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';

import type { CleanNotificationWithRelations } from '~/database/supabase/notifications/types.ts';
import { APP_SHELL_QUERY_KEY } from '~/server/app/app-shell-data-query.ts';
import type { AppShellData } from '~/server/app/get-app-shell-data.ts';
import type { NotificationsPageData } from '~/server/notifications/get-notifications-page-data.ts';
import { NOTIFICATIONS_PAGE_QUERY_KEY } from '~/server/notifications/notifications-page-data-query.ts';

import countViewedNotifications from './count-viewed-notifications.ts';

export const NAVBAR_NOTIFICATIONS_QUERY_KEY = ['navbar-notifications'] as const;

export function getNavbarNotificationsQueryOptions() {
	return queryOptions<CleanNotificationWithRelations[]>({
		queryFn: async () => {
			const response = await fetch('/api/supabase/get-user-notifications', {
				redirect: 'follow',
			});

			return (await response.json()) as CleanNotificationWithRelations[];
		},
		queryKey: NAVBAR_NOTIFICATIONS_QUERY_KEY,
	});
}

type NotificationsUpdater =
	| CleanNotificationWithRelations[]
	| ((currentNotifications: CleanNotificationWithRelations[]) => CleanNotificationWithRelations[]);

export function setNotificationsQueryData(queryClient: QueryClient, notificationsOrUpdater: NotificationsUpdater) {
	const currentNotifications =
		queryClient.getQueryData<CleanNotificationWithRelations[]>(NAVBAR_NOTIFICATIONS_QUERY_KEY) ?? [];
	const nextNotifications =
		typeof notificationsOrUpdater === 'function'
			? notificationsOrUpdater(currentNotifications)
			: notificationsOrUpdater;
	const notificationCount = countViewedNotifications(nextNotifications);

	queryClient.setQueryData<CleanNotificationWithRelations[]>(NAVBAR_NOTIFICATIONS_QUERY_KEY, nextNotifications);
	queryClient.setQueryData<NotificationsPageData | undefined>(NOTIFICATIONS_PAGE_QUERY_KEY, (currentData) =>
		currentData
			? {
					...currentData,
					notifications: nextNotifications,
				}
			: currentData,
	);
	queryClient.setQueryData<AppShellData | undefined>(APP_SHELL_QUERY_KEY, (currentData) =>
		currentData
			? {
					...currentData,
					notificationCount,
					notifications: nextNotifications,
				}
			: currentData,
	);

	if (typeof document !== 'undefined') {
		document.title = notificationCount !== 0 ? `(${notificationCount}) Eqity` : 'Eqity';
	}

	return nextNotifications;
}

export async function refetchNotificationsQueryData(queryClient: QueryClient) {
	const notifications = await queryClient.fetchQuery(getNavbarNotificationsQueryOptions());

	setNotificationsQueryData(queryClient, notifications);

	return notifications;
}
